import Checkbox from '@material-ui/core/Checkbox'
import Tooltip from '@material-ui/core/Tooltip'
import {ResponseError} from 'lib/ui/api-client'
import {onChangeCheckedHandler} from 'lib/dom'
import {
  NOTIFY_RECORDING_READY,
  Permission,
  UPDATE_TEAM,
  VIEW_RECORDINGS,
  useCanUpdatePermission,
  usePermissions,
} from 'organization/PermissionsProvider'
import {
  Role,
  useAddPermission,
  useRoles,
  useRemovePermission,
} from 'organization/Team/Roles/RolesProvider'
import React from 'react'

export default function PermissionCheckbox(props: {
  role: Role
  permission: Permission
  processing: boolean
  setProcessing: (processing: boolean) => void
  onError: (error: ResponseError) => void
}) {
  const {updateRole} = useRoles()
  const hasPermission = props.role.permissions.includes(props.permission)
  const {can} = usePermissions()
  const add = useAddPermission()
  const remove = useRemovePermission()
  const canUpdatePermission = useCanUpdatePermission(props.permission)
  const recordingNotificationDisabled = isRecordingNotificationDisabled(
    props.role,
    props.permission,
  )
  const disabled = !canUpdatePermission || recordingNotificationDisabled

  const updatePermission = (isAdd: boolean) => {
    if (props.processing || !can(UPDATE_TEAM) || disabled) {
      return
    }

    const update = isAdd ? add : remove

    props.setProcessing(true)
    update(props.role, props.permission)
      .then((updatedRole) => {
        updateRole(updatedRole)
        return clearRecordingNotification(
          props.permission,
          updatedRole,
          isAdd,
          remove,
        )
      })
      .then(updateRole)
      .catch(props.onError)
      .finally(() => {
        props.setProcessing(false)
      })
  }

  const checkbox = (
    <Checkbox
      disabled={disabled}
      checked={hasPermission}
      onChange={onChangeCheckedHandler(updatePermission)}
      color="primary"
      inputProps={{
        'aria-label': 'toggle permission',
      }}
      disableRipple
    />
  )

  if (!recordingNotificationDisabled) {
    return checkbox
  }

  return (
    <Tooltip title={RECORDING_NOTIFICATION_REQUIRES_VIEW}>
      <span>{checkbox}</span>
    </Tooltip>
  )
}

const RECORDING_NOTIFICATION_REQUIRES_VIEW =
  'Turn on View Recordings before enabling Recordings Ready Notification.'

function isRecordingNotificationDisabled(role: Role, permission: Permission) {
  if (permission !== NOTIFY_RECORDING_READY) {
    return false
  }

  const hasViewRecordings = role.permissions.includes(VIEW_RECORDINGS)
  const hasNotification = role.permissions.includes(NOTIFY_RECORDING_READY)

  return !hasViewRecordings && !hasNotification
}

function clearRecordingNotification(
  permission: Permission,
  role: Role,
  isAdd: boolean,
  remove: (role: Role, permission: Permission) => Promise<Role>,
) {
  const turnedOffViewRecordings = !isAdd && permission === VIEW_RECORDINGS
  const hasNotification = role.permissions.includes(NOTIFY_RECORDING_READY)

  if (!turnedOffViewRecordings || !hasNotification) {
    return role
  }

  return remove(role, NOTIFY_RECORDING_READY)
}
